import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { Theme } from '@mui/material'
import { ApiError } from '../postingRoot.reducer'

import {
  clearRequests, getTopicsAndMetadataForCurrentUser,
  requestFailed,
  requestFinished,
  requestStarted,
  setGeneralPostingOptions,
  setMessage, setPostingConfiguration,
  setSocialMediaNetworks,
  setTheme, setUser,
} from './common.actions'
import { ActionCall, SocialMediaNetwork, GeneralPostingOptions } from '@professocial/professocialTypes/types/commonTypes'
import getAppTheme from '../../defaults/getAppTheme'
import { produce } from 'immer'

import { Message } from '@professocial/professocialTypes/types/messageTypes'
import { TopicsWithMetadataResponse } from '@3m5/sm_publish-frontend-api/models'
import { PostingConfiguration } from '@professocial/professocialTypes/types/configurationTypes'

export interface CommonState {
  theme: Theme
  user?: any
  message?: Message
  generalPostingOptions: GeneralPostingOptions
  postingConfiguration?: PostingConfiguration
  socialMediaNetworks: SocialMediaNetwork
  requests: ActionCall[]
  fetching: boolean
  aboTopics?: TopicsWithMetadataResponse
  error: ApiError
}

const INITIAL_STATE: CommonState = {
  theme: getAppTheme(),
  error: {},
  fetching: false,
  generalPostingOptions: {
    allowLinkParsing: true,
    allowTemplateEdit: false,
    allowLinkIndividualisation: false,
    allowIndividualHashtags: false,
    allowTags: false,
    allowImageUpload: false,
    allowVideoUpload: false,
    templateSelection: false,
    showAsPopUp: false,
    showPreview: false,
    useMediaGallery: false,
  },
  socialMediaNetworks: {
    facebook: true,
    instagram: true,
    googleMyBusiness: true,
  },
  requests: [],
}

const triggerProfessocialMessage = (data: Message) => {
  window.dispatchEvent(new CustomEvent('ProfessocialMessage', { detail: data }))
}

export const CommonReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setTheme, (state, theme) => {
    return ({ ...state, theme })
  })
  .case(setUser, (state, user) => {
    return ({ ...state, user })
  })
  .case(setPostingConfiguration, (state, postingConfiguration) => {
    return ({ ...state, postingConfiguration })
  })
  .case(setGeneralPostingOptions, (state, generalPostingOptions) => {
    return ({ ...state, generalPostingOptions })
  })
  .case(setSocialMediaNetworks, (state, socialMediaNetworks) => {
    return ({ ...state, socialMediaNetworks })
  })
  /**
   * Message Management
   */
  .case(setMessage, (state, message) => {
    // Fix - wait while the reducer is executing
    setTimeout(() => triggerProfessocialMessage(message))

    return produce(state, (draft) => {
      draft.message = message
    })
  })

  /**
   *  Requests Management
   */
  .case(requestStarted, (state, action) => {
    return produce(state, (draft) => {
      draft.requests.splice(draft.requests.findIndex(request => request.status === 'success'), 1) // filter success state out
      draft.requests.push({ type: action.type, status: 'pending', inProgress: true, error: null, id: action.id, network: action.network })
    })
  })
  .case(requestFinished, (state, action) => {
    return produce(state, (draft) => {
      const index = draft.requests.findIndex(request => request.type === action.type && request.id === action.id)
      if (index !== -1) {
        draft.requests[index].inProgress = false
        draft.requests[index].status = 'success'
        draft.requests[index].error = null
      }
      setTimeout(() => action.message && triggerProfessocialMessage({
        severity: action.message.severity,
        message: action.message.message,
        eventType: action.message.eventType,
      }))
    })
  })
  .case(requestFailed, (state, action) => {
    return produce(state, (draft) => {
      const index = draft.requests.findIndex(request => request.type === action.type && request.id === action.id)
      if (index !== -1) {
        draft.requests[index].inProgress = false
        draft.requests[index].status = 'failed'
        draft.requests[index].error = action.error
        draft.requests[index].id = undefined
      }
      setTimeout(() => action.message && triggerProfessocialMessage({
        severity: action.message.severity,
        message: action.message.message,
        eventType: action.message.eventType,
      }))
    })
  })
  .case(clearRequests, (state, action) => {
    return produce(state, (draft) => {
      draft.requests = []
    })
  })

  /**
   * getTopicsAndMetadataForCurrentUser
   */
  .case(getTopicsAndMetadataForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getTopicsAndMetadataForCurrentUser
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboTopics = result
      draft.fetching = false
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getTopicsAndMetadataForCurrentUser = (error as Response)
    })
  })
